/**
 * Saved views plus the filter builder — the strip above every list.
 *
 * A view is a named bundle of URL state. Selecting one rewrites the query
 * string; saving one captures whatever the query string currently holds. Because
 * the URL is the state, there is nothing to keep in sync between "the view I
 * picked" and "the rows I am looking at".
 */

import { useState } from 'react'
import { useFetcher, useNavigate, useSearchParams } from 'react-router-dom'
import type { SavedView, FieldRegistry, ViewEntity } from '@erp/contracts'
import { readFilters, withFilters } from '../view-state'
import { FilterBuilder } from './filter-builder'
import { Button, TextInput } from '../ui'
import { api } from '../api'

export function ViewBar({
  entity,
  registry,
  views,
}: {
  entity: ViewEntity
  registry: FieldRegistry
  views: readonly SavedView[]
}) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const fetcher = useFetcher()
  const [showFilters, setShowFilters] = useState(false)

  const activeId = searchParams.get('view')
  const active = views.find((view) => view.id === activeId)
  const filters = readFilters(searchParams)
  const groupBy = searchParams.get('groupBy')

  /** Applying a view replaces filter state wholesale rather than merging. */
  function select(view: SavedView | null) {
    const next = new URLSearchParams()
    const search = searchParams.get('q')
    if (search) next.set('q', search)
    if (view) {
      next.set('view', view.id)
      if (view.filters.length > 0) next.set('filters', JSON.stringify(view.filters))
      const primary = view.sort[0]
      if (primary) {
        next.set('sort', primary.field)
        next.set('direction', primary.direction)
      }
      // A kanban view carries its axis; the board reads groupBy straight off the
      // URL, so selecting the view is all it takes to regroup.
      if (view.viewType === 'kanban' && view.groupByField !== null) {
        next.set('groupBy', view.groupByField)
      }
    }
    navigate({ search: next.toString() })
  }

  /**
   * Dirty when the URL's filters differ from the saved view's. Compared by
   * serialisation, which is exact enough for a set the user just built and
   * avoids a deep-equal helper.
   */
  const dirty =
    active !== undefined && JSON.stringify(active.filters) !== JSON.stringify(filters)

  return (
    <div className="ax-view-bar">
      <nav className="ax-view-tabs">
        <button
          type="button"
          className={activeId === null ? 'ax-view-tab ax-view-tab-active' : 'ax-view-tab'}
          onClick={() => select(null)}
        >
          All
        </button>
        {views.map((view) => (
          <button
            key={view.id}
            type="button"
            className={view.id === activeId ? 'ax-view-tab ax-view-tab-active' : 'ax-view-tab'}
            onClick={() => select(view)}
          >
            {view.name}
            {view.isShared ? <span className="ax-view-shared" title="Shared" /> : null}
          </button>
        ))}
      </nav>

      <div className="ax-view-actions">
        <Button onClick={() => setShowFilters(!showFilters)}>
          {filters.length > 0 ? `Filters (${filters.length})` : 'Filters'}
        </Button>

        {dirty ? (
          <fetcher.Form method="post">
            <input type="hidden" name="intent" value="view.update" />
            <input type="hidden" name="viewId" value={active.id} />
            <input type="hidden" name="filters" value={JSON.stringify(filters)} />
            <Button type="submit" disabled={fetcher.state !== 'idle'}>
              Save changes
            </Button>
          </fetcher.Form>
        ) : null}

        {filters.length > 0 || groupBy !== null ? (
          <fetcher.Form method="post" className="ax-view-save">
            <input type="hidden" name="intent" value="view.create" />
            <input type="hidden" name="entity" value={entity} />
            <input type="hidden" name="filters" value={JSON.stringify(filters)} />
            {/* Saving from the board captures its axis, so reopening the view
                restores the same columns. */}
            <input type="hidden" name="viewType" value={groupBy === null ? 'list' : 'kanban'} />
            <input type="hidden" name="groupByField" value={groupBy ?? ''} />
            <TextInput name="name" placeholder="Name this view" />
            <label className="ax-checkbox">
              <input type="checkbox" name="isShared" value="true" />
              Share
            </label>
            <Button type="submit" disabled={fetcher.state !== 'idle'}>
              Save as view
            </Button>
          </fetcher.Form>
        ) : null}

        {active && !active.isShared ? (
          <fetcher.Form method="post">
            <input type="hidden" name="intent" value="view.delete" />
            <input type="hidden" name="viewId" value={active.id} />
            <button type="submit" className="ax-link-danger">
              Delete view
            </button>
          </fetcher.Form>
        ) : null}
      </div>

      {showFilters ? <FilterBuilder registry={registry} /> : null}
    </div>
  )
}

/**
 * The three view intents, shared by every list route's action. Returns null when
 * the intent is not one of these.
 */
export async function handleViewIntent(
  intent: string,
  form: FormData,
): Promise<{ ok: boolean; message?: string } | null> {
  const parsed: unknown = JSON.parse(String(form.get('filters') ?? '[]'))
  const filters = Array.isArray(parsed) ? (parsed as SavedView['filters']) : []

  if (intent === 'view.create') {
    await api.views.create({
      name: String(form.get('name') ?? '').trim() || 'Untitled view',
      entity: String(form.get('entity')) as ViewEntity,
      viewType: (form.get('viewType') === 'kanban' ? 'kanban' : 'list') as 'kanban' | 'list',
      groupByField: (String(form.get('groupByField') ?? '') || null),
      filters,
      sort: [],
      columns: [],
      kanbanSettings: DEFAULT_KANBAN,
      isShared: form.get('isShared') === 'true',
      isPinned: false,
      position: 0,
    })
    return { ok: true }
  }

  if (intent === 'view.update') {
    const id = String(form.get('viewId'))
    const existing = await api.views.get(id)
    // Only the filter set changes here; everything else keeps its stored value.
    await api.views.update(id, { ...toUpsert(existing), filters })
    return { ok: true }
  }

  if (intent === 'view.delete') {
    await api.views.remove(String(form.get('viewId')))
    return { ok: true }
  }

  return null
}

const DEFAULT_KANBAN = {
  columnOrder: [],
  collapsed: [],
  showEmptyColumns: true,
  cardFields: [],
}

/** A saved view minus its server-owned fields, which is what upsert accepts. */
function toUpsert(view: SavedView) {
  const { id: _id, ownerId: _ownerId, createdAt: _c, updatedAt: _u, ...rest } = view
  return rest
}
